import { create } from 'zustand'
import { useChatStore } from './chat-store'

/**
 * The docked right-edge config rail: one 300px column (CONFIG_RAIL_WIDTH) that
 * a single panel config surface owns at a time. Google Sheets
 * model: opening a chart's config swaps out the companion drawer and
 * closing it brings the companion back.
 *
 * Ownership is by id (the panel id). Six chart panels each render their own
 * config surface, so `activeId` is what keeps only one of them portaled into
 * the rail: a surface whose `open` is true but whose id is not `activeId`
 * closes itself.
 *
 * `containerEl` is the portal target the rail host registers on desktop only.
 * With no container (mobile, or the host not mounted) surfaces fall back to a
 * full-width sheet and the rail never touches the companion.
 */
interface RightRailState {
  containerEl: HTMLElement | null
  activeId: string | null
  setContainerEl: (el: HTMLElement | null) => void
  /** Claim the rail for `id` (takes it over from a previous owner). */
  open: (id: string) => void
  /** Release the rail, only if `id` still owns it. */
  close: (id: string) => void
  /** Test helper: back to the initial state. */
  reset: () => void
}

// Whether the companion drawer was open when the rail took the column, so the
// rail can hand it back on close. Module state on purpose: it is not UI state.
let restoreCompanion = false

export const useRightRailStore = create<RightRailState>((set, get) => ({
  containerEl: null,
  activeId: null,

  setContainerEl: (el) => set({ containerEl: el }),

  open: (id) => {
    const { containerEl, activeId } = get()
    if (containerEl && activeId == null) {
      const chat = useChatStore.getState()
      if (chat.open && chat.surface === 'drawer') {
        restoreCompanion = true
        chat.setOpen(false)
      }
    }
    set({ activeId: id })
  },

  close: (id) => {
    if (get().activeId !== id) return
    set({ activeId: null })
    if (restoreCompanion) {
      restoreCompanion = false
      useChatStore.getState().setOpen(true)
    }
  },

  reset: () => {
    restoreCompanion = false
    set({ containerEl: null, activeId: null })
  },
}))

// Mutual exclusion the other way: the user opening the companion drawer while
// a config surface holds the rail releases the rail (the surface closes itself
// via `activeId`). The rail's own restore sets `open` after `activeId` is
// already null, so it never trips this.
useChatStore.subscribe((state, prev) => {
  if (!state.open || prev.open || state.surface !== 'drawer') return
  const rail = useRightRailStore.getState()
  if (rail.activeId == null || !rail.containerEl) return
  restoreCompanion = false
  useRightRailStore.setState({ activeId: null })
})
